import { useRecoilValue } from 'recoil';
import DateRangeIcon from '@mui/icons-material/DateRange';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { UrlParams } from 'common/types';
import { useSetSearchParams } from 'hooks/useSetSearchParams';

import Dropdown from 'components/common/Dropdown/Dropdown';
import { ButtonVariant } from 'components/common/Button/Button';
import { searchParamRecordState, searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { extractFacetExpression } from 'common/functions';

const dateRangeIcon = <DateRangeIcon fontSize="small" />;

type CompareDropdownProps = {
  variant?: ButtonVariant;
};

/**
 * This dropdown allows the user to compare the results of the current search to those for
 * a different time period.
 *
 * @prop variant    variant for dropdown button
 */
function CompareDropdown({
  variant,
}: CompareDropdownProps): JSX.Element {

  const setSearchParams  = useSetSearchParams();
  const searchParams = useRecoilValue(searchParamRecordState);
  const compareTimeOffset = useRecoilValue(searchParamState(K.URL_PARAM.COMPARE_TIME_OFFSET));

  const handleCompareItemClick = (time: string): void => {
    const currentShowGraphs = searchParams.showGraphs && JSON.parse(searchParams.showGraphs);
    const facetExp = extractFacetExpression(searchParams.facet || '');
    setSearchParams({
      [K.URL_PARAM.COMPARE_TIME_OFFSET]: time,
      [K.URL_PARAM.SHOW_GRAPHS]: '',
      [K.URL_PARAM.HIDE_GRAPHS]: '',
      ...(searchParams.showGraphs && searchParams.showGraphs.length &&
        {[K.URL_PARAM.FACET]: `${currentShowGraphs.pop()}(${facetExp.facetVariable || ''})`}),
    } as UrlParams);
  };

  const compareMenuSections = [
    [
      {
        text:         'Prev hour',
        clickHandler: (): void => handleCompareItemClick('1h'),
        testId:       ELEMENT.searchGraphComparePrevHour,
      },
      {
        text:         'Prev 4 hours',
        clickHandler: (): void => handleCompareItemClick('4h'),
        testId:       ELEMENT.searchGraphComparePrev4h,
      },
      {
        text:         'Prev day',
        clickHandler: (): void => handleCompareItemClick('1d'),
        testId:       ELEMENT.searchGraphComparePrevDay,
      },
      {
        text:         'Prev week',
        clickHandler: (): void => handleCompareItemClick('1w'),
        testId:       ELEMENT.searchGraphComparePrevWeek,
      },
      {
        text:         'Prev 2 weeks',
        clickHandler: (): void => handleCompareItemClick('2w'),
        testId:       ELEMENT.searchGraphComparePrev2w,
      },
    ],
    [
      {
        text:         "Don't compare",
        clickHandler: (): void => handleCompareItemClick(''),
        testId:       ELEMENT.searchGraphCompareNone,
      },
    ],
  ];

  const dropdownText = (): string => {
    switch (compareTimeOffset) {
      case '1h':
        return 'Compare: Prev Hour';
      case '4h':
        return 'Compare: Prev 4 Hour';
      case '1d':
        return 'Compare: Prev Day';
      case '1w':
        return 'Compare: Prev Week';
      case '2w':
        return 'Compare: Prev 2 weeks';
      default:
        return 'Compare';
    }
  };

  return (
    <Dropdown
      sections={compareMenuSections}
      leftIcon={dateRangeIcon}
      variant={variant}
      testId={ELEMENT.searchGraphCompareButton}
    >
      {dropdownText()}
    </Dropdown>
  );
}

export default CompareDropdown;
